// ── "You have an unsent draft" — MIS forms ───────────────────────────────────
// Pinned above a MIS list so a half-written invoice, quotation, stock request or packing list is
// something you SEE rather than something you have to remember. The draft lives in this browser
// only (tools/hooks/useFormDraft.js) - nothing was saved to the server, no document number was
// taken - so it is deliberately not styled as a row of the list: it is a reminder, not a record.
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { listDrafts, clearDraft } from '../digitalMarketing/draftStore';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import ConfirmDialog from '../../tools/modal/confirmDialog';

// The four kinds of MIS draft: what a form stores it under, and what the strip calls it.
export const MIS_DRAFT = {
  invoice:     { kind: 'misInvoice',     labelKey: 'mis.draftKindInvoice' },
  quote:       { kind: 'misQuote',       labelKey: 'mis.draftKindQuote' },
  request:     { kind: 'misRequest',     labelKey: 'mis.draftKindRequest' },
  packingList: { kind: 'misPackingList', labelKey: 'mis.draftKindPackingList' },
};
const KIND_LABEL = Object.fromEntries(Object.values(MIS_DRAFT).map((d) => [d.kind, d.labelKey]));

/**
 * A fresh key for a form that has just been opened. Every opening gets its OWN draft: two forms of
 * the same kind never overwrite each other, so a draft someone chose to keep is never replaced by a
 * newer one. A form continued from the strip re-uses that draft's own key instead.
 */
export const newDraftKey = (name, branchId) => (
  `mis:${name}:${branchId || 'none'}:${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`
);

const relTime = (ts, t) => {
  if (!ts) return '';
  const mins = Math.round((Date.now() - ts) / 60000);
  if (mins < 1) return t('dm.draftJustNow');
  if (mins < 60) return t('dm.draftMinutesAgo', { count: mins });
  const hours = Math.round(mins / 60);
  if (hours < 24) return t('dm.draftHoursAgo', { count: hours });
  return new Date(ts).toLocaleDateString();
};

/**
 * @param kinds       which kinds to show: ['misInvoice', ...] - see MIS_DRAFT
 * @param branchId    only this branch's drafts (a draft belongs to the branch it was started in)
 * @param onOpen      called with the stored draft when the person wants to carry on
 * @param refreshKey  bump it to re-read after a form closes
 */
export default function MisDraftStrip({ kinds, branchId, onOpen, T, refreshKey }) {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState([]);
  const [confirmKey, setConfirmKey] = useState(null);

  const reload = useCallback(async () => {
    const all = await Promise.all(kinds.map((k) => listDrafts(k)));
    setDrafts(all.flat()
      .filter((d) => !branchId || !d.branchId || String(d.branchId) === String(branchId))
      .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kinds.join('|'), branchId]);

  useEffect(() => { reload(); }, [reload, refreshKey]);

  // Another tab may have sent or discarded one: re-read when this tab is looked at again.
  useEffect(() => {
    const onFocus = () => reload();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [reload]);

  const discard = async () => {
    const key = confirmKey;
    setConfirmKey(null);
    if (!key) return;
    await clearDraft(key);
    reload();
  };

  if (!drafts.length) return null;

  return (
    <>
      <Box sx={{ px: 1, pt: 0.5 }}>
        {drafts.map((d) => (
          <Box key={d.key} data-testid="mis-draft" sx={{
            display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75,
            px: 1.5, py: 1, borderRadius: '12px',
            border: `1px dashed ${T.BD2 || T.BD}`, bgcolor: 'rgba(255,193,7,0.08)',
          }}>
            <EditNoteIcon sx={{ fontSize: 20, color: '#B26A00', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#B26A00',
                textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('mis.draftUnsent', { kind: t(KIND_LABEL[d.kind] || 'mis.draftKindInvoice') })}
              </Typography>
              <Typography noWrap sx={{ fontSize: '0.8rem', color: T.TEXT_PRI }}>
                {d.title || t('mis.draftUntitled')}
              </Typography>
              <Typography sx={{ fontSize: '0.66rem', color: T.TEXT_TER }}>{relTime(d.updatedAt, t)}</Typography>
            </Box>
            <Button size="small" variant="contained" onClick={() => onOpen(d)}
              sx={{ textTransform: 'none', borderRadius: '8px', flexShrink: 0, fontSize: '0.72rem' }}>
              {t('dm.draftContinue')}
            </Button>
            <Tooltip title={t('common.discard')}>
              <IconButton size="small" aria-label={t('common.discard')} onClick={() => setConfirmKey(d.key)}
                sx={{ color: T.TEXT_TER, flexShrink: 0 }}>
                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Box>

      <ConfirmDialog
        open={Boolean(confirmKey)}
        onClose={() => setConfirmKey(null)}
        onConfirm={discard}
        title={t('mis.draftDiscardTitle')}
        message={t('mis.draftDiscardMessage')}
        confirmLabel={t('common.discard')}
        destructive
      />
    </>
  );
}

/**
 * What closing a half-filled NEW document asks. The form has already been keeping a draft, so the
 * question is not "discard your changes?" but what to do with that draft: keep it (it is waiting in
 * the list), throw it away, or go back to the form.
 * z-index: raised like the forms themselves, because a form can sit above modal level.
 */
export function DraftCloseDialog({ open, onKeep, onDiscard, onCancel }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onCancel} sx={{ zIndex: (th) => th.zIndex.modal + 3 }}
      PaperProps={{ sx: { borderRadius: '14px', minWidth: 320, backgroundImage: 'none' } }}>
      <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 700 }}>{t('mis.draftCloseTitle')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: '0.82rem' }}>{t('mis.draftCloseMessage')}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 0.5 }}>
        <Button size="small" onClick={onCancel} sx={{ textTransform: 'none' }}>{t('mis.draftBackButton')}</Button>
        <Button size="small" color="error" onClick={onDiscard} sx={{ textTransform: 'none', color: '#EA005A' }}>{t('mis.draftDiscardButton')}</Button>
        <Button size="small" variant="contained" onClick={onKeep} sx={{ textTransform: 'none' }}>{t('mis.draftKeepButton')}</Button>
      </DialogActions>
    </Dialog>
  );
}

